const states = new WeakMap();
const openPickers = new Set();

// Keep Vue's calendar in its original DOM tree. The browser top layer escapes
// overflow/transform containment without losing inherited theme or form state.
export function admin2026FloatingDatepickerDirective(fallback) {
    return {
        mounted(el, binding) {
            const inDrawer = !!el.closest('.admin2026-drawer-panel, .admin2026-drawer-surface');
            const floatInDrawer = inDrawer && !!el.closest('[data-admin2026-goal-drawer-datepicker]');
            // Headless UI portals move dialogs outside the admin shell.
            const adminDialog = document.body.hasAttribute('data-admin2026-geometry-style')
                ? el.closest('[role="dialog"][aria-modal="true"]') : null;
            if ((!el.closest('.admin2026-shell') && !adminDialog) || (inDrawer && !floatInDrawer)) {
                fallback.mounted?.(el, binding);
                return;
            }
            const picker = binding.instance;
            const popup = picker.LitepieRef;
            const nativePopover = typeof popup.showPopover === 'function';
            const fallbackContainer = el.closest('[id^="headlessui-dialog-panel-"]') || adminDialog || document.body;
            const parent = popup.parentNode;
            const sibling = popup.nextSibling;
            el.dataset.admin2026FloatingDatepickerHost = 'true';
            popup.dataset.admin2026FloatingDatepicker = 'true';
            if (nativePopover) popup.setAttribute('popover', 'manual');
            let frame = 0;
            let opener;
            let wasOpen;
            const position = () => {
                frame = 0;
                if (!picker.isShow || !el.isConnected) return;
                const viewport = window.visualViewport;
                const leftEdge = (viewport?.offsetLeft || 0) + 8;
                const topEdge = (viewport?.offsetTop || 0) + 8;
                const width = viewport?.width || window.innerWidth;
                const height = viewport?.height || window.innerHeight;
                popup.style.maxWidth = `${Math.max(0, width - 16)}px`;
                popup.style.maxHeight = `${Math.max(0, height - 16)}px`;
                const anchor = (opener || el).getBoundingClientRect();
                const box = popup.getBoundingClientRect();
                const left = Math.max(leftEdge, Math.min(anchor.left + anchor.width / 2 - box.width / 2, leftEdge + width - 16 - box.width));
                const below = anchor.bottom + 10;
                const above = anchor.top - 10 - box.height;
                const top = below + box.height <= topEdge + height - 16 ? below
                    : above >= topEdge ? above : Math.max(topEdge, topEdge + (height - 16 - box.height) / 2);
                popup.style.left = `${left}px`;
                popup.style.top = `${top}px`;
            };
            const schedule = () => {
                if (!frame && picker.isShow) frame = requestAnimationFrame(position);
            };
            const click = (event) => {
                const target = event.target;
                if (popup.contains(target)) {
                    if (target.closest('.away-cancel-picker, .away-apply-picker, .litepie-shortcuts')) picker.isShow = false;
                } else if (el.contains(target)) {
                    opener = target.closest('button, input') || el;
                    wasOpen = picker.isShow;
                } else {
                    picker.isShow = false;
                }
            };
            const openFromTrigger = (event) => {
                if (el.contains(event.target) && !popup.contains(event.target) && picker.isShow === wasOpen) {
                    picker.isShow = true;
                }
            };
            const keydown = (event) => {
                if (event.key === 'Escape' && picker.isShow) {
                    picker.isShow = false;
                    opener?.focus();
                    event.preventDefault();
                    event.stopImmediatePropagation();
                }
            };
            const stop = picker.$watch('isShow', (open) => {
                if (!open) {
                    openPickers.delete(picker);
                    if (nativePopover && popup.matches(':popover-open')) popup.hidePopover();
                    return;
                }
                for (const other of openPickers) other.isShow = false;
                openPickers.add(picker);
                picker.$nextTick(() => {
                    if (!picker.isShow || !el.isConnected) return;
                    if (nativePopover) popup.showPopover();
                    else fallbackContainer.appendChild(popup);
                    position();
                });
            });
            const observer = new ResizeObserver(schedule);
            observer.observe(popup);
            window.addEventListener('resize', schedule);
            document.addEventListener('scroll', schedule, true);
            window.visualViewport?.addEventListener('resize', schedule);
            window.visualViewport?.addEventListener('scroll', schedule);
            document.addEventListener('click', click, true);
            el.addEventListener('click', openFromTrigger);
            document.addEventListener('keydown', keydown, true);
            states.set(el, () => {
                stop();
                observer.disconnect();
                cancelAnimationFrame(frame);
                openPickers.delete(picker);
                window.removeEventListener('resize', schedule);
                document.removeEventListener('scroll', schedule, true);
                window.visualViewport?.removeEventListener('resize', schedule);
                window.visualViewport?.removeEventListener('scroll', schedule);
                document.removeEventListener('click', click, true);
                el.removeEventListener('click', openFromTrigger);
                document.removeEventListener('keydown', keydown, true);
                if (nativePopover && popup.matches(':popover-open')) popup.hidePopover();
                if (!nativePopover && popup.parentNode !== parent) parent.insertBefore(popup, sibling);
            });
        },
        updated(el, binding) {
            if (!states.has(el)) fallback.updated?.(el, binding);
        },
        beforeUnmount(el) {
            states.get(el)?.();
        },
        unmounted(el, binding) {
            if (!states.has(el)) fallback.unmounted?.(el, binding);
            states.delete(el);
        },
    };
}
